// src/sim/map/pathfinding.ts — A* over a walkability grid (MAP-13, RESEARCH Pattern 5).
//
// Invariants:
//   - No side effects on caller's grid; PF.Grid is constructed fresh per query.
//     CRITICAL (RESEARCH Pitfall 1): PF.Grid is mutated during findPath —
//     reusing the same PF.Grid across queries returns wrong paths silently.
//   - pathfinding has no DOM/Phaser/timer deps; safe in src/sim/**.
//   - Diagonal movement enabled EXCEPT through wall corners (DiagonalMovement.OnlyWhenNoObstacles).
//   - Octile heuristic — correct for 8-directional grid movement.
//
// Source: Phase 1 RESEARCH.md §Pattern 5, PATTERNS.md §pathfinding.ts.

import PF from 'pathfinding';
import type { WalkabilityGrid } from '../../shared/types/map';

const finder = new PF.AStarFinder({
  diagonalMovement: PF.DiagonalMovement.OnlyWhenNoObstacles,
  heuristic: PF.Heuristic.octile,
});

/**
 * Optional circular obstacle (e.g., the plinth ring). Tiles within
 * `radiusPx` pixels of `centerPx` are treated as non-walkable so the
 * returned path routes AROUND the obstacle instead of through it.
 *
 * Source: debug session cascade-prevention (2026-04-30) — without ring
 * awareness in A*, freed hiders' deep/move paths kept routing through
 * the solid ring and stalling at its boundary.
 */
export interface RingObstacle {
  centerPx: { x: number; y: number };
  radiusPx: number;
  tileWidthPx: number;
  tileHeightPx: number;
}

/**
 * Returns A* path from (fromX, fromY) → (toX, toY) as tile-coord pairs.
 * Returns [] if unreachable (blocked endpoints or no connection).
 *
 * If `ringObstacle` is provided, tiles whose centres lie within the ring
 * radius are pre-marked non-walkable on a copy of the grid before A* runs,
 * so paths route around the ring. The endpoints themselves are exempt
 * (so a hider already at the ring boundary can still produce a path
 * starting there).
 */
export function findTilePath(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  grid: WalkabilityGrid,
  ringObstacle?: RingObstacle | null,
): Array<[number, number]> {
  // MANDATORY FRESH GRID — see Pitfall 1.
  const pfGrid = new PF.Grid(grid);

  if (ringObstacle) {
    const { centerPx, radiusPx, tileWidthPx, tileHeightPx } = ringObstacle;
    const radiusSq = radiusPx * radiusPx;
    // Bounding box of ring in tile coords — only iterate the candidate range.
    const minTx = Math.max(0, Math.floor((centerPx.x - radiusPx) / tileWidthPx));
    const maxTx = Math.min(grid[0]!.length - 1, Math.ceil((centerPx.x + radiusPx) / tileWidthPx));
    const minTy = Math.max(0, Math.floor((centerPx.y - radiusPx) / tileHeightPx));
    const maxTy = Math.min(grid.length - 1, Math.ceil((centerPx.y + radiusPx) / tileHeightPx));
    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        // Skip the source/dest tiles so they remain reachable.
        if ((tx === fromX && ty === fromY) || (tx === toX && ty === toY)) continue;
        const cx = tx * tileWidthPx + tileWidthPx / 2;
        const cy = ty * tileHeightPx + tileHeightPx / 2;
        const dx = cx - centerPx.x;
        const dy = cy - centerPx.y;
        if (dx * dx + dy * dy < radiusSq) {
          pfGrid.setWalkableAt(tx, ty, false);
        }
      }
    }
  }

  return finder.findPath(fromX, fromY, toX, toY, pfGrid) as Array<[number, number]>;
}
